import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { WhatsAppIcon } from "../components/common/WhatsAppIcon";
import { Reveal } from "../components/motion/Reveal";
import { Marquee } from "../components/common/Marquee";
import { SectionHead } from "../components/common/SectionHead";
import { RoomCard } from "../components/common/RoomCard";
import { PhotoBreak } from "../components/common/PhotoBreak";
import { FinalCta } from "../components/common/FinalCta";
import { IMAGES } from "../data/site";
import { NEDEN_BIZ } from "../data/nedenBiz";
import { useSiteData, useC, useSite, useWa, fileUrl } from "../context/SiteData";
import { getMapEmbedUrl } from "../lib/maps";

const lineAnim = (delay) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});



const Hero = ({ c, images, loaded }) => {
  const ref = useRef(null);
  const wa = useWa();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);

  const heroSrc = images.hero ? fileUrl(images.hero) : loaded ? IMAGES.hero : null;
  const lines = [c.hero_title_1, c.hero_title_2, c.hero_title_3];

  return (
    <section ref={ref} data-testid="hero" className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink-950">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        {heroSrc && (
          <motion.img
            src={heroSrc}
            alt="Yalı Yurt binası"
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        )}
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink-950/85 via-ink-950/35 to-ink-950/30" />

      <div className="container-x relative pb-20 pt-40 sm:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <p className="label flex items-center gap-3 text-cream-50/70" data-testid="hero-label">
            <span className="inline-block h-px w-10 bg-cream-50/50" />
            {c.hero_label}
          </p>
        </motion.div>

        <h1 className="mt-6 max-w-4xl text-4xl font-bold leading-[1.08] tracking-tight text-cream-50 sm:text-5xl lg:text-[4.2rem]" data-testid="hero-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0.45 + i * 0.13)}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-6 max-w-xl text-base leading-relaxed text-cream-50/75 sm:text-lg"
        >
          {c.hero_sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-9"
        >
          <Link
            to="/nedenbiz"
            data-testid="hero-cta-nedenbiz"
            className="btn-light !px-9 !py-[1.2rem] !text-[15px] !tracking-[0.16em]"
          >
            Neden Biz?
            <ArrowRight size={17} />
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          className="mt-4 flex flex-col gap-3 sm:flex-row"
        >
          <Link to="/odalar" data-testid="hero-cta-explore" className="btn-light">
            Yurdu Keşfet
            <ArrowRight size={15} />
          </Link>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-whatsapp"
            className="btn-outline-light"
          >
            <WhatsAppIcon size={15} />
            WhatsApp'tan Bilgi Al
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const Home = () => {
  const { images, gallery, loaded } = useSiteData();
  const c = useC();
  const site = useSite();
  const introSrc = images.intro ? fileUrl(images.intro) : loaded ? IMAGES.intro : null;
  const breakSrc = images.photoBreak ? fileUrl(images.photoBreak) : loaded ? IMAGES.photoBreak : null;
  const videoPoster = images.videoPoster ? fileUrl(images.videoPoster) : loaded ? IMAGES.videoPoster : null;

  return (
    <>
      <Hero c={c} images={images} loaded={loaded} />
      <Marquee />

      {/* Intro */}
      <section data-testid="intro-section" className="section-y">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead label={c.intro_label} title={c.intro_title} />
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-ink-500 sm:text-lg">{c.intro_text}</p>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
                <Link to="/hakkimizda" data-testid="intro-about-link" className="link-arrow">
                  {c.intro_link}
                  <ArrowRight size={15} />
                </Link>
                <Link
                  to="/nedenbiz"
                  data-testid="intro-neden-biz"
                  className="btn-outline-dark"
                >
                  Neden Biz?
                  <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="group overflow-hidden">
              {introSrc ? (
                <img
                  src={introSrc}
                  alt="Yalı Yurt yaşam alanı"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover saturate-[0.92] transition-[transform,filter] duration-700 group-hover:scale-[1.02] group-hover:saturate-100"
                />
              ) : (
                <div className="aspect-[4/3] w-full animate-pulse bg-cream-200" />
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section data-testid="stats-section" className="bg-ink-900">
        <div className="container-x grid grid-cols-2 gap-y-12 py-16 sm:py-20 md:grid-cols-3 lg:grid-cols-5">
          {c.stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className={i === c.stats.length - 1 ? "col-span-2 md:col-span-1" : ""}>
              <div className="group border-l border-line-dark pl-5 transition-colors duration-300 hover:border-cream-50/40">
                <p className="text-3xl font-bold tracking-tight text-cream-50 sm:text-4xl" data-testid={`stat-value-${i}`}>
                  {s.value}
                </p>
                <p className="mt-2 text-xs font-medium uppercase tracking-[0.16em] text-cream-50/55 transition-colors duration-300 group-hover:text-cream-50/80">
                  {s.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="section-y">
        <div className="container-x">
          <SectionHead
            label={c.values_label}
            title={c.values_title}
            desc={c.values_desc}
          />
          <div className="mt-14 grid gap-px overflow-hidden border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {c.values.map((v, i) => (
              <Reveal key={v.no} delay={i * 0.08} className="h-full">
                <div className="group flex h-full flex-col bg-cream-50 p-7 transition-colors duration-300 hover:bg-ink-900 sm:p-8" data-testid={`value-item-${i}`}>
                  <span className="text-xs font-bold tracking-[0.2em] text-ink-400 transition-colors duration-300 group-hover:text-cream-50/50">{v.no}</span>
                  <h3 className="mt-5 text-xl font-bold tracking-tight text-ink-900 transition-colors duration-300 group-hover:text-cream-50">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-500 transition-colors duration-300 group-hover:text-cream-50/60">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Neden Biz */}
      <section id="neden-biz" data-testid="neden-biz" className="section-y bg-cream-200">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHead
              label="Neden Biz?"
              title="17 Yıllık Deneyim, Güvenli ve Konforlu Öğrenci Yaşamı"
              desc="Samsun'da 17 yıllık deneyimli kadromuzla üniversite öğrencilerine güvenli, düzenli ve konforlu bir yaşam alanı sunuyoruz. Öğrencilerimizin yalnızca konaklama değil, kendilerini güvende ve rahat hissedebilecekleri bir ortamda yaşamalarını önemsiyoruz."
            />
            <Reveal delay={0.2} className="shrink-0">
              <Link to="/nedenbiz" data-testid="neden-biz-details-link" className="btn-outline-dark">
                Neden Biz?
                <ArrowUpRight size={15} aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-px overflow-hidden border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {NEDEN_BIZ.map((v, i) => (
              <Reveal key={v.title} delay={Math.min(i * 0.06, 0.3)} className="h-full">
                <div className="group flex h-full flex-col bg-cream-50 p-7 transition-colors duration-300 hover:bg-ink-900 sm:p-8" data-testid={`neden-item-${i}`}>
                  <span className="text-xs font-bold tracking-[0.2em] text-ink-400 transition-colors duration-300 group-hover:text-cream-50/50">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-5 text-lg font-bold tracking-tight text-ink-900 transition-colors duration-300 group-hover:text-cream-50">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-500 transition-colors duration-300 group-hover:text-cream-50/60">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms preview */}
      <section data-testid="rooms-section" className="section-y bg-cream-200">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHead
              label={c.rooms_label}
              title={c.rooms_title}
              desc={c.rooms_desc}
            />
            <Reveal delay={0.2}>
              <Link to="/odalar" data-testid="rooms-all-link" className="btn-outline-dark shrink-0">
                Tüm Odalar
                <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            {c.rooms.map((room, i) => (
              <RoomCard key={room.slug} room={room} delay={i * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Services list */}
      <section data-testid="services-section" className="section-y">
        <div className="container-x">
          <SectionHead
            label={c.services_label}
            title={c.services_title}
            desc={c.services_desc}
          />
          <div className="mt-12 border-t border-line">
            {c.services.map((s, i) => (
              <Reveal key={s.no} delay={Math.min(i * 0.04, 0.2)}>
                <Link
                  to="/hizmetler"
                  data-testid={`service-row-${s.no}`}
                  className="group grid grid-cols-[auto_1fr] items-baseline gap-5 border-b border-line py-6 transition-colors duration-300 hover:bg-cream-100 sm:grid-cols-[80px_260px_1fr] sm:gap-8 sm:px-4"
                >
                  <span className="text-xs font-bold tracking-[0.2em] text-ink-400 transition-colors duration-300 group-hover:text-ink-900">{s.no}</span>
                  <h3 className="text-lg font-bold tracking-tight text-ink-900 transition-transform duration-300 group-hover:translate-x-1.5 sm:text-xl">{s.title}</h3>
                  <p className="col-span-2 text-sm leading-relaxed text-ink-500 sm:col-span-1">{s.desc}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <PhotoBreak image={breakSrc} quote={c.photo_break_quote} />

      {/* Gallery preview */}
      <section data-testid="gallery-preview" className="section-y">
        <div className="container-x">
          <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <SectionHead label={c.gallery_label} title={c.gallery_title} />
            <Reveal delay={0.2}>
              <Link to="/galeri" data-testid="gallery-all-link" className="btn-outline-dark shrink-0">
                Tüm Galeri
                <ArrowUpRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {(() => {
              const spans = ["col-span-2 row-span-2 aspect-auto", "aspect-square", "aspect-square", "col-span-2 aspect-[2/1]"];
              if (gallery === null) {
                return spans.map((sp, i) => (
                  <div key={i} className={`${sp.split(" aspect")[0]} animate-pulse bg-cream-200 ${i === 0 ? "row-span-2" : "aspect-square"}`} />
                ));
              }
              const preview = (gallery.length >= 4 ? gallery.slice(0, 4).map((g) => ({ src: fileUrl(g.path), alt: g.alt })) : null)
                || (loaded ? [
                  { src: introSrc, alt: "Yaşam alanı" },
                  { src: IMAGES.study, alt: "Çalışma ortamı" },
                  { src: IMAGES.food, alt: "Ev yemekleri" },
                  { src: IMAGES.building, alt: "Yurt binası" },
                ].filter((g) => g.src) : []);
              return preview.map((g, i) => (
                <Reveal key={g.src} delay={i * 0.05} className={spans[i].split(" aspect")[0]}>
                  <Link to="/galeri" className="group relative block h-full overflow-hidden">
                    <img src={g.src} alt={g.alt} loading="lazy" className={`${i === 0 ? "h-full" : spans[i].includes("aspect") ? spans[i].match(/aspect-\[[^\]]+\]|aspect-square/)[0] : ""} w-full object-cover saturate-[0.92] transition-[transform,filter] duration-700 group-hover:scale-[1.03] group-hover:saturate-100`} />
                    <span className="pointer-events-none absolute inset-0 bg-ink-950/0 transition-colors duration-300 group-hover:bg-ink-950/20" />
                  </Link>
                </Reveal>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Video */}
      <section data-testid="video-section" className="bg-ink-900">
        <div className="container-x section-y">
          <SectionHead
            light
            label={c.video_label}
            title={c.video_title}
            desc={c.video_desc}
          />
          <Reveal delay={0.15}>
            <Link
              to="/videolar"
              data-testid="video-preview-link"
              className="group relative mt-12 block overflow-hidden"
            >
              <div className="aspect-video w-full overflow-hidden">
                {videoPoster ? (
                  <img
                    src={videoPoster}
                    alt="Yalı Yurt tanıtım filmi"
                    loading="lazy"
                    className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                ) : (
                  <div className="h-full w-full animate-pulse bg-ink-800" />
                )}
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="flex h-20 w-20 items-center justify-center rounded-full border border-cream-50/40 bg-ink-950/40 text-cream-50 backdrop-blur-sm transition-all duration-300 group-hover:scale-110 group-hover:bg-cream-50 group-hover:text-ink-900">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5-11-6.5z" /></svg>
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Location preview */}
      <section data-testid="location-section" className="section-y">
        <div className="container-x grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionHead
              label={c.location_label}
              title={c.location_title.split("\n")}
              desc={c.location_desc}
            />
            <Reveal delay={0.2}>
              <div className="mt-8 grid grid-cols-2 gap-px border border-line bg-line">
                {c.home_distances.map((d) => (
                  <div key={d.place} className="group bg-white p-5 transition-colors duration-300 hover:bg-ink-900">
                    <p className="text-2xl font-bold tracking-tight text-ink-900 transition-colors duration-300 group-hover:text-cream-50">{d.time}</p>
                    <p className="mt-1 text-xs font-medium uppercase tracking-[0.14em] text-ink-400 transition-colors duration-300 group-hover:text-cream-50/60">{d.place}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <Link to="/konum" data-testid="location-link" className="link-arrow mt-8">
                Konumu İncele
                <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden border border-line">
              <iframe
                title="Yalı Yurt Konum"
                src={getMapEmbedUrl(c)}
                className="map-grayscale aspect-[4/3] w-full"
                loading="lazy"
                data-testid="location-map-preview"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Contact teaser */}
      <section data-testid="contact-teaser" className="border-t border-line bg-cream-100">
        <div className="container-x section-y flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <Reveal>
              <h2 className="text-3xl font-bold tracking-tight text-ink-900 sm:text-4xl">{c.contact_teaser_title}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-ink-500 sm:text-lg">
                {c.contact_teaser_text}
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="flex flex-col gap-3 sm:flex-row">
            <a href={site.phoneHref} data-testid="teaser-phone" className="btn-outline-dark">{site.phone}</a>
            <Link to="/iletisim" data-testid="teaser-contact" className="btn-primary">İletişim Formu</Link>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
};

export default Home;
